import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const BANNER_IMAGE =
  'https://roadtechservices.netlify.app/images/contactbannerimg-850w.jpeg';
const MAP_IMAGE =
  'https://roadtechservices.netlify.app/images/map.PNG';

const testimonials = [
  {
    text: 'Road Tech Services saved me so much time! Instead of hauling my mower to a shop, they came right to my house and fixed it the same day. Highly recommended.',
    name: 'Mark R.',
    role: 'Homeowner',
    rating: 5,
  },
  {
    text: 'Fantastic service — my snow blower was up and running within an hour of arrival. Professional, knowledgeable, and very convenient.',
    name: 'Lisa M.',
    role: 'Michigan Resident',
    rating: 5,
  },
];

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function Contact() {
  const [formStatus, setFormStatus] = useState<FormStatus>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });
    try {
      const res = await fetch(
        'https://readdy.ai/api/form/d6tcanncrcujsk0v1720',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: data.toString(),
        }
      );
      if (res.ok) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <div className="font-body">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-24 min-h-[380px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src={BANNER_IMAGE}
            alt="Contact Road Tech Services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/65" />
        </div>
        <div className="relative z-10 text-center px-6">
          <p className="text-orange-400 text-xs font-bold uppercase tracking-widest mb-3">
            Get in Touch
          </p>
          <h1 className="font-display text-5xl lg:text-7xl font-bold text-white uppercase tracking-tight">
            Contact Us
          </h1>
        </div>
      </section>

      {/* Contact Section */}
      <section className="bg-white py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-3">
              Here to Help
            </p>
            <h2 className="font-display text-4xl lg:text-5xl font-bold uppercase tracking-tight text-neutral-900 mb-4">
              Get in Touch
            </h2>
            <p className="text-neutral-500 text-base max-w-xl mx-auto leading-relaxed">
              Whether you need a lawn mower tune-up, snow blower repair, or
              generator maintenance, Road Tech Services comes to you. We handle
              repairs on-site, often the same day if parts are available.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left: Info + Map */}
            <div>
              <div className="space-y-5 mb-8">
                <a
                  href="mailto:Road.tech7@gmail.com"
                  className="flex items-start gap-4 p-5 bg-stone-50 rounded-xl border border-stone-100 hover:border-orange-200 transition-colors cursor-pointer group"
                >
                  <div className="w-11 h-11 flex items-center justify-center bg-orange-100 group-hover:bg-orange-600 rounded-xl transition-colors flex-shrink-0">
                    <i className="ri-mail-line text-orange-600 group-hover:text-white text-lg transition-colors" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm mb-0.5">
                      Email
                    </div>
                    <div className="text-neutral-600 text-sm">
                      Road.tech7@gmail.com
                    </div>
                  </div>
                </a>

                <a
                  href="tel:248-781-0600"
                  className="flex items-start gap-4 p-5 bg-stone-50 rounded-xl border border-stone-100 hover:border-orange-200 transition-colors cursor-pointer group"
                >
                  <div className="w-11 h-11 flex items-center justify-center bg-orange-100 group-hover:bg-orange-600 rounded-xl transition-colors flex-shrink-0">
                    <i className="ri-phone-line text-orange-600 group-hover:text-white text-lg transition-colors" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm mb-0.5">
                      Phone
                    </div>
                    <div className="text-neutral-600 text-sm">
                      (248) 781-0600
                    </div>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-5 bg-stone-50 rounded-xl border border-stone-100">
                  <div className="w-11 h-11 flex items-center justify-center bg-orange-100 rounded-xl flex-shrink-0">
                    <i className="ri-map-pin-line text-orange-600 text-lg" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm mb-0.5">
                      Service Area
                    </div>
                    <div className="text-neutral-600 text-sm">
                      Michigan, United States
                    </div>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="rounded-xl overflow-hidden border border-stone-200 h-64">
                <img
                  src={MAP_IMAGE}
                  alt="Road Tech Services service area in Michigan"
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="mt-6 p-5 bg-orange-50 border border-orange-100 rounded-xl">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-phone-line text-orange-600 text-lg" />
                  </div>
                  <span className="font-semibold text-neutral-900 text-sm">
                    Need Remote Troubleshooting?
                  </span>
                </div>
                <p className="text-neutral-600 text-sm leading-relaxed mb-3">
                  Not in our service area? Book a live phone or video
                  consultation with Matt for expert guidance.
                </p>
                <Link
                  to="/consultation"
                  className="text-orange-600 hover:text-orange-700 text-sm font-semibold transition-colors cursor-pointer"
                >
                  Book a Consultation Call →
                </Link>
              </div>
            </div>

            {/* Right: Form */}
            <div>
              <div className="bg-stone-50 rounded-2xl p-8 border border-stone-100">
                <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-neutral-900 mb-2">
                  Send a Message
                </h3>
                <p className="text-neutral-500 text-sm mb-6">
                  Describe your equipment issue and we&rsquo;ll get back to you
                  promptly.
                </p>

                {formStatus === 'success' ? (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                    <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-3">
                      <i className="ri-check-line text-green-600 text-xl" />
                    </div>
                    <h4 className="font-semibold text-neutral-900 mb-2">
                      Message Sent!
                    </h4>
                    <p className="text-neutral-600 text-sm">
                      Thanks for reaching out. We&rsquo;ll be in touch shortly.
                    </p>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    id="contact-form"
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                        Name <span className="text-orange-600">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="John / Jane Doe"
                        className="w-full px-4 py-3 bg-white border border-stone-200 rounded-lg text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-orange-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                        Email <span className="text-orange-600">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="name@example.com"
                        className="w-full px-4 py-3 bg-white border border-stone-200 rounded-lg text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-orange-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="+1 (248) 781-0600"
                        className="w-full px-4 py-3 bg-white border border-stone-200 rounded-lg text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-orange-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                        How Did You Find Us
                      </label>
                      <input
                        type="text"
                        name="find-us"
                        placeholder="Google, Referral, Social Media..."
                        className="w-full px-4 py-3 bg-white border border-stone-200 rounded-lg text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-orange-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                        Message <span className="text-orange-600">*</span>
                      </label>
                      <textarea
                        name="Message"
                        required
                        rows={4}
                        maxLength={500}
                        placeholder="Hello, I need help with my..."
                        className="w-full px-4 py-3 bg-white border border-stone-200 rounded-lg text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-orange-500 transition-colors resize-none"
                      />
                    </div>

                    {formStatus === 'error' && (
                      <p className="text-red-600 text-sm">
                        Something went wrong. Please try again or call us
                        directly.
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-semibold py-3.5 rounded-lg transition-colors text-sm cursor-pointer whitespace-nowrap"
                    >
                      {formStatus === 'submitting'
                        ? 'Sending...'
                        : 'Submit Message'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-stone-50 py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-3">
              Client Feedback
            </p>
            <h2 className="font-display text-4xl font-bold uppercase tracking-tight text-neutral-900">
              What Our Customers Say
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl mx-auto">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className="bg-white rounded-xl p-6 border border-stone-200"
              >
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: t.rating }).map((_, si) => (
                    <i
                      key={si}
                      className="ri-star-fill text-amber-400 text-sm"
                    />
                  ))}
                </div>
                <p className="text-neutral-600 text-sm leading-relaxed italic mb-5">
                  &ldquo;{t.text}&rdquo;
                </p>
                <div>
                  <div className="font-semibold text-neutral-900 text-sm">
                    {t.name}
                  </div>
                  <div className="text-neutral-400 text-xs mt-0.5">
                    {t.role}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
